// Browser side of the direct-to-S3 uploads (server: packages/api/src/routers/storage.ts).
// Framework-free so it can be tested without a DOM: the transport (the app's oRPC
// storage procedures) and the PUT itself (XHR, for upload progress) are injected.
//
// One UploadJob per file. `run` creates the upload session, PUTs every part with
// retries, then asks the server to verify and complete it. A job that failed keeps
// its session and the parts already sent, so running it again resumes: expired part
// URLs are re-signed, a session the server no longer knows starts over.

export type UploadPart = { partNumber: number; size: number; url: string };
export type UploadSession = {
  id: string;
  parts: UploadPart[];
  partSize: number;
  headers: Record<string, string | undefined>;
};
export type UploadedFile = {
  id: string;
  purpose: string;
  name: string;
  type: string;
  size: number;
  /** empty for private files */
  url: string;
};
type Outcome = { ok: boolean; reason: string };

/** The app's storage procedures (client.storage.*), with the purpose already checked */
export type UploadTransport = {
  create: (input: {
    purpose: string;
    name: string;
    type: string;
    size: number;
  }) => Promise<Outcome & { upload: UploadSession | null }>;
  signParts: (input: {
    id: string;
    partNumbers: number[];
  }) => Promise<Outcome & { parts: UploadPart[] }>;
  complete: (input: {
    id: string;
    parts: { partNumber: number; etag: string }[];
  }) => Promise<Outcome & { file: UploadedFile | null }>;
  abort: (input: { id: string }) => Promise<Outcome>;
};

export type PutRequest = {
  url: string;
  body: Blob;
  headers: Record<string, string | undefined>;
  onProgress: (loaded: number) => void;
  signal: AbortSignal;
};
/** status 0: the request never got an answer (offline, DNS, CORS, stalled) */
export type PutResult = { status: number; etag: string | null };
export type PutFunction = (request: PutRequest) => Promise<PutResult>;

export type UploadPhase = "preparing" | "uploading" | "retrying" | "offline" | "processing";

export type UploadSnapshot = {
  phase: UploadPhase;
  loaded: number;
  total: number;
  /** bytes per second, smoothed */
  speed: number;
  /** seconds left at the current speed; null until there is a speed */
  eta: number | null;
  /** the automatic retry being waited for (1-based), 0 when none */
  attempt: number;
  /** when that retry starts (ms since epoch) */
  retryAt: number | null;
};

/** `retryable`: trying again later may work (the network), unlike a refusal by policy */
export class UploadError extends Error {
  constructor(
    message: string,
    readonly retryable: boolean,
  ) {
    super(message);
    this.name = "UploadError";
  }
}

export const isAbortError = (error: Error) => error.name === "AbortError";

const abortError = () => new DOMException("the upload was canceled", "AbortError");

/** Resolves after `ms`, rejects as soon as `signal` aborts */
export function sleep(ms: number, signal: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(abortError());
      return;
    }
    const timer = setTimeout(done, ms);
    function done() {
      signal.removeEventListener("abort", stop);
      resolve();
    }
    function stop() {
      clearTimeout(timer);
      reject(abortError());
    }
    signal.addEventListener("abort", stop, { once: true });
  });
}

/** 1s, 2s, 4s, 8s … capped at 30s, with ±25% jitter so many clients do not retry in step */
export const backoffDelay = (attempt: number, random = Math.random()) =>
  Math.round(Math.min(30_000, 1000 * 2 ** (attempt - 1)) * (0.75 + random * 0.5));

const RETRY_STATUSES = new Set([0, 408, 429, 500, 502, 503, 504]);

const typesByExtension = new Map([
  ["jpg", "image/jpeg"],
  ["jpeg", "image/jpeg"],
  ["jfif", "image/jpeg"],
  ["png", "image/png"],
  ["webp", "image/webp"],
  ["gif", "image/gif"],
  ["avif", "image/avif"],
  ["heic", "image/heic"],
  ["pdf", "application/pdf"],
  ["mp4", "video/mp4"],
  ["m4v", "video/mp4"],
  ["webm", "video/webm"],
  ["mov", "video/quicktime"],
]);

/**
 * The file's real MIME type: Windows reports "" for unknown extensions and some
 * systems still say image/pjpeg or image/jpg for JPEG.
 */
export function fileType(file: { name: string; type: string }) {
  if (file.type === "image/jpg" || file.type === "image/pjpeg") return "image/jpeg";
  if (file.type) return file.type;
  const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
  return typesByExtension.get(extension) ?? "application/octet-stream";
}

/** The status an oRPC (or fetch) failure carries; 0 when no answer came back */
function answerStatus(error: Error) {
  if (!("status" in error)) return 0;
  const status = Number(error.status);
  return Number.isInteger(status) ? status : 0;
}

function transportFailure(error: Error) {
  const status = answerStatus(error);
  if (status === 401) return new UploadError("نشست شما تمام شده است؛ دوباره وارد شوید.", false);
  if (status === 403) return new UploadError("اجازه بارگذاری این فایل را ندارید.", false);
  if (status >= 400 && status < 500)
    return new UploadError("سرور درخواست بارگذاری را نپذیرفت.", false);
  return new UploadError("ارتباط با سرور برقرار نشد.", true);
}

const putFailure = (status: number) =>
  status === 0
    ? "ارسال فایل قطع شد؛ اتصال اینترنت را بررسی کنید."
    : status === 403
      ? "مهلت ارسال این فایل تمام شد؛ دوباره تلاش کنید."
      : `فضای ذخیره‌سازی فایل را نپذیرفت (کد ${status}).`;

export type UploadJobOptions = {
  purpose: string;
  transport: UploadTransport;
  put: PutFunction;
  onChange: (snapshot: UploadSnapshot) => void;
  /** parts sent at once */
  concurrency?: number;
  /** tries per request before the job fails (manual retry resumes it) */
  maxAttempts?: number;
  /** whether the browser thinks it is online; waits for `online` when it is not */
  isOnline?: () => boolean;
  waitOnline?: (signal: AbortSignal) => Promise<void>;
  /** ms before automatic retry number `attempt`; backoffDelay by default */
  retryDelay?: (attempt: number) => number;
};

const browserOnline = () => globalThis.navigator?.onLine !== false;

const waitForBrowserOnline = (signal: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const online = () => {
      signal.removeEventListener("abort", stop);
      resolve();
    };
    const stop = () => {
      globalThis.removeEventListener("online", online);
      reject(abortError());
    };
    globalThis.addEventListener("online", online, { once: true });
    signal.addEventListener("abort", stop, { once: true });
  });

export class UploadJob {
  private session: UploadSession | null = null;
  private readonly etags = new Map<number, string>();
  private readonly inflight = new Map<number, number>();
  private snapshot: UploadSnapshot;
  private sample = { at: 0, loaded: 0 };
  private lastEmit = 0;
  private readonly concurrency: number;
  private readonly maxAttempts: number;
  private readonly isOnline: () => boolean;
  private readonly waitOnline: (signal: AbortSignal) => Promise<void>;
  private readonly retryDelay: (attempt: number) => number;

  constructor(
    readonly file: File,
    private readonly options: UploadJobOptions,
  ) {
    this.concurrency = options.concurrency ?? 4;
    this.maxAttempts = options.maxAttempts ?? 5;
    this.isOnline = options.isOnline ?? browserOnline;
    this.waitOnline = options.waitOnline ?? waitForBrowserOnline;
    this.retryDelay = options.retryDelay ?? ((attempt) => backoffDelay(attempt));
    this.snapshot = {
      phase: "preparing",
      loaded: 0,
      total: file.size,
      speed: 0,
      eta: null,
      attempt: 0,
      retryAt: null,
    };
  }

  /** Uploads (or resumes) the file; rejects with UploadError, or an AbortError on cancel */
  async run(signal: AbortSignal): Promise<UploadedFile> {
    this.sample = { at: 0, loaded: this.sentBytes() };
    const session = await this.openSession(signal);
    await this.sendParts(session, signal);
    this.update({ phase: "processing", attempt: 0, retryAt: null, eta: null }, true);
    const completed = await this.withRetries(
      () =>
        this.options.transport.complete({
          id: session.id,
          // a single PUT has nothing to stitch: its ETag may be unreadable (CORS) and is unused
          parts:
            session.parts.length > 1
              ? [...this.etags].map(([partNumber, etag]) => ({ partNumber, etag }))
              : [],
        }),
      signal,
      "processing",
    );
    if (!completed.ok || !completed.file) {
      // the server dropped the upload when it refused it: a retry starts over
      this.reset();
      throw new UploadError(completed.reason || "فایل تأیید نشد.", false);
    }
    return completed.file;
  }

  /** Cancel for good: the server forgets the session and whatever bytes it has */
  async discard() {
    const session = this.session;
    this.reset();
    if (session) await this.options.transport.abort({ id: session.id }).catch(() => undefined);
  }

  private reset() {
    this.session = null;
    this.etags.clear();
    this.inflight.clear();
  }

  private async openSession(signal: AbortSignal): Promise<UploadSession> {
    const current = this.session;
    if (current) {
      const missing = current.parts
        .filter((part) => !this.etags.has(part.partNumber))
        .map((part) => part.partNumber);
      if (missing.length === 0) return current;
      // a resume: the URLs may have expired while the job waited
      const signed = await this.withRetries(
        () => this.options.transport.signParts({ id: current.id, partNumbers: missing }),
        signal,
        "preparing",
      );
      if (signed.ok) {
        // only the URLs are new; the byte ranges stay the ones the session was created with
        current.parts = current.parts.map((part) => ({
          ...part,
          url: signed.parts.find((item) => item.partNumber === part.partNumber)?.url ?? part.url,
        }));
        return current;
      }
      this.reset();
    }
    this.update({ phase: "preparing", loaded: 0 }, true);
    const created = await this.withRetries(
      () =>
        this.options.transport.create({
          purpose: this.options.purpose,
          name: this.file.name,
          type: fileType(this.file),
          size: this.file.size,
        }),
      signal,
      "preparing",
    );
    if (!created.ok || !created.upload) {
      throw new UploadError(created.reason || "بارگذاری آغاز نشد.", false);
    }
    this.session = created.upload;
    if (signal.aborted) {
      // canceled while the session was being created: do not leave it open
      await this.discard();
      throw abortError();
    }
    return created.upload;
  }

  private async sendParts(session: UploadSession, signal: AbortSignal) {
    const queue = session.parts.filter((part) => !this.etags.has(part.partNumber));
    // one failing part stops its siblings instead of letting them run on
    const stop = new AbortController();
    const onAbort = () => stop.abort();
    signal.addEventListener("abort", onAbort, { once: true });
    let next = 0;
    const worker = async () => {
      while (next < queue.length) {
        const part = queue[next];
        next += 1;
        if (part) await this.sendPart(session, part, stop.signal);
      }
    };
    try {
      await Promise.all(
        Array.from({ length: Math.min(this.concurrency, queue.length) }, () =>
          worker().catch((error: Error) => {
            stop.abort();
            throw error;
          }),
        ),
      );
    } finally {
      signal.removeEventListener("abort", onAbort);
      this.inflight.clear();
    }
    if (signal.aborted) throw abortError();
  }

  private async sendPart(session: UploadSession, part: UploadPart, signal: AbortSignal) {
    let url = part.url;
    let resigned = false;
    for (let attempt = 1; ; attempt += 1) {
      if (!this.isOnline()) {
        this.update({ phase: "offline", retryAt: null }, true);
        await this.waitOnline(signal);
      }
      this.update({ phase: "uploading", attempt: 0, retryAt: null }, true);
      const start = (part.partNumber - 1) * session.partSize;
      const result = await this.options.put({
        url,
        body: this.file.slice(start, start + part.size),
        headers: session.headers,
        signal,
        onProgress: (loaded) => {
          this.inflight.set(part.partNumber, Math.min(loaded, part.size));
          this.progress();
        },
      });
      this.inflight.delete(part.partNumber);
      if (signal.aborted) throw abortError();

      if (result.status >= 200 && result.status < 300) {
        // parts are stitched together by their ETags; S3 must expose the header (CORS)
        if (session.parts.length > 1 && !result.etag) {
          throw new UploadError("پاسخ فضای ذخیره‌سازی ناقص بود؛ دوباره تلاش کنید.", true);
        }
        this.etags.set(part.partNumber, result.etag ?? "");
        this.progress(true);
        return;
      }
      if (result.status === 403 && !resigned) {
        // most often an expired URL: sign this part again once, free of charge
        resigned = true;
        attempt -= 1;
        const signed = await this.withRetries(
          () =>
            this.options.transport.signParts({ id: session.id, partNumbers: [part.partNumber] }),
          signal,
          "uploading",
        );
        const fresh = signed.parts.find((item) => item.partNumber === part.partNumber);
        if (!signed.ok || !fresh) throw new UploadError(signed.reason || putFailure(403), true);
        url = fresh.url;
        continue;
      }
      if (result.status === 0 && !this.isOnline()) {
        // the connection dropped: waiting for it does not use up a try
        attempt -= 1;
        continue;
      }
      if (!RETRY_STATUSES.has(result.status) || attempt >= this.maxAttempts) {
        throw new UploadError(
          putFailure(result.status),
          RETRY_STATUSES.has(result.status) || result.status === 403,
        );
      }
      const delay = this.retryDelay(attempt);
      this.update({ phase: "retrying", attempt, retryAt: Date.now() + delay }, true);
      await sleep(delay, signal);
    }
  }

  /**
   * A transport call, retried while the network (not the server's policy) fails it.
   * Offline, it waits for the connection without spending a try, then shows `phase` again.
   */
  private async withRetries<T>(
    call: () => Promise<T>,
    signal: AbortSignal,
    phase: UploadPhase,
  ): Promise<T> {
    for (let attempt = 1; ; attempt += 1) {
      if (signal.aborted) throw abortError();
      if (!this.isOnline()) {
        this.update({ phase: "offline", retryAt: null }, true);
        await this.waitOnline(signal);
        this.update({ phase, attempt: 0, retryAt: null }, true);
      }
      try {
        return await call();
      } catch (error) {
        if (!(error instanceof Error)) throw error;
        if (isAbortError(error)) throw error;
        const failure = transportFailure(error);
        if (failure.retryable && !this.isOnline()) {
          attempt -= 1;
          continue;
        }
        if (!failure.retryable || attempt >= this.maxAttempts) throw failure;
        const delay = this.retryDelay(attempt);
        this.update({ phase: "retrying", attempt, retryAt: Date.now() + delay }, true);
        await sleep(delay, signal);
        this.update({ phase, attempt: 0, retryAt: null }, true);
      }
    }
  }

  private sentBytes() {
    let sent = 0;
    for (const part of this.session?.parts ?? []) {
      if (this.etags.has(part.partNumber)) sent += part.size;
    }
    for (const loaded of this.inflight.values()) sent += loaded;
    return sent;
  }

  /** Recomputes loaded, speed and ETA; `force` emits even inside the 100 ms throttle */
  private progress(force = false) {
    const loaded = this.sentBytes();
    const now = Date.now();
    const elapsed = now - this.sample.at;
    if (this.sample.at === 0 || loaded < this.sample.loaded) {
      // first sample, or a part restarted after a failure: measure again from here
      this.sample = { at: now, loaded };
    } else if (elapsed >= 400) {
      const instant = ((loaded - this.sample.loaded) * 1000) / elapsed;
      const speed = this.snapshot.speed === 0 ? instant : this.snapshot.speed * 0.7 + instant * 0.3;
      this.sample = { at: now, loaded };
      this.snapshot.speed = speed;
    }
    const speed = this.snapshot.speed;
    this.update(
      {
        loaded,
        eta: speed > 0 ? Math.max(0, (this.snapshot.total - loaded) / speed) : null,
      },
      force,
    );
  }

  private update(change: Partial<UploadSnapshot>, force = false) {
    this.snapshot = { ...this.snapshot, ...change };
    const now = Date.now();
    if (!force && now - this.lastEmit < 100) return;
    this.lastEmit = now;
    this.options.onChange({ ...this.snapshot });
  }
}

/** No byte moved for this long means a dead connection, even if the socket says otherwise */
const STALL_MS = 45_000;

/** PUT with upload progress (fetch cannot report it); resolves status 0 on network failure */
export const xhrPut: PutFunction = (request) =>
  new Promise<PutResult>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let lastProgress = Date.now();
    let settled = false;
    const finish = (result: PutResult | null) => {
      if (settled) return;
      settled = true;
      clearInterval(watchdog);
      request.signal.removeEventListener("abort", cancel);
      if (result) resolve(result);
      else reject(abortError());
    };
    const cancel = () => {
      xhr.abort();
      finish(null);
    };
    const watchdog = setInterval(() => {
      if (Date.now() - lastProgress > STALL_MS) {
        xhr.abort();
        finish({ status: 0, etag: null });
      }
    }, 5_000);
    xhr.open("PUT", request.url);
    for (const [name, value] of Object.entries(request.headers)) {
      if (value !== undefined) xhr.setRequestHeader(name, value);
    }
    xhr.upload.onprogress = (event) => {
      lastProgress = Date.now();
      request.onProgress(event.loaded);
    };
    xhr.onload = () => finish({ status: xhr.status, etag: xhr.getResponseHeader("ETag") });
    xhr.onerror = () => finish({ status: 0, etag: null });
    xhr.onabort = () => finish(request.signal.aborted ? null : { status: 0, etag: null });
    if (request.signal.aborted) {
      cancel();
    } else {
      request.signal.addEventListener("abort", cancel, { once: true });
      xhr.send(request.body);
    }
  });

// ---------------------------------------------------------------------------
// Persian wording for sizes, speeds and time left
// ---------------------------------------------------------------------------

const fa = new Intl.NumberFormat("fa-IR", { maximumFractionDigits: 1 });
const faWhole = new Intl.NumberFormat("fa-IR", { maximumFractionDigits: 0 });
const KiB = 1024;
const MiB = 1024 * 1024;

/** ۱٫۵ مگابایت */
export function formatBytes(bytes: number) {
  if (bytes >= 1024 * MiB) return `${fa.format(bytes / (1024 * MiB))} گیگابایت`;
  if (bytes >= MiB) return `${fa.format(bytes / MiB)} مگابایت`;
  if (bytes >= KiB) return `${faWhole.format(bytes / KiB)} کیلوبایت`;
  return `${faWhole.format(bytes)} بایت`;
}

/** ۱٫۲ مگابایت بر ثانیه */
export const formatSpeed = (bytesPerSecond: number) => `${formatBytes(bytesPerSecond)} بر ثانیه`;

/** «حدود ۲ دقیقه مانده» style estimates, rounded the way people read them */
export function formatEta(seconds: number) {
  if (seconds < 5) return "چند لحظه";
  if (seconds < 60) return `${faWhole.format(Math.ceil(seconds / 5) * 5)} ثانیه`;
  if (seconds < 3600) return `${faWhole.format(Math.ceil(seconds / 60))} دقیقه`;
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.ceil((seconds % 3600) / 60);
  return minutes > 0 && minutes < 60
    ? `${faWhole.format(hours)} ساعت و ${faWhole.format(minutes)} دقیقه`
    : `${faWhole.format(hours + (minutes >= 60 ? 1 : 0))} ساعت`;
}

export const formatPercent = (loaded: number, total: number) =>
  `${faWhole.format(total > 0 ? Math.floor((loaded / total) * 100) : 0)}٪`;
